import { QueryObserverResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import { MantineColumn, MantineTable } from '../../../../../react-commons/components/table/MantineTable';
import { QueryBaseComponent } from '../../../../../react-commons/query/QueryBaseComponent';
import { ResourceType } from '../../../resource/enumerations/ResourceType';
import {
    ContentPropertyValidation,
    ContentResource,
    useContentActions,
} from '../../../resource/resources/artifacts/content/contentResource';
import { EditDeleteButtons } from '../../reusable-components/base/EditDeleteButtons';
import { ControlledPropertyInputs } from '../../reusable-components/base/property-bases/read-only/ControlledPropertyInputs';
import { useDeleteResourceDialogFunction } from '../../reusable-components/dialogs/UseDeleteResourceDialogFunction';

export const ContentsTableComponent = ({
    openDrawerAtElement,
    contentsQuery,
}: {
    openDrawerAtElement: (elementId: string) => void;
    activitiesModelId: string;
    contentsQuery: QueryObserverResult<Array<ContentResource> | null>;
}) => {
    const deleteResourceDialogFunction = useDeleteResourceDialogFunction();
    const contentActions = useContentActions();

    const deleteContent = contentActions.useDeleteResource();

    const columns = useMemo((): Array<MantineColumn<ContentResource>> => {
        return [
            {
                Header: null,
                type: 'string',
                accessorKey: 'modelElement',
                header: 'Model Element',
                Cell: ({ row: { original } }) => (
                    <ControlledPropertyInputs.String
                        readOnly={true}
                        nullable={false}
                        value={original.modelElement}
                        style={{ paddingLeft: 0 }}
                        hideLabel={true}
                        propertyValidationInformation={{
                            resourceType: ResourceType.CONTENT,
                            propertyIdentifier: ContentPropertyValidation.MODEL_ELEMENT,
                            resourceId: undefined,
                        }}
                    />
                ),
            },
            {
                Header: null,
                type: 'string',
                accessorKey: 'name',
                header: 'Name',
                Cell: ({ row: { original } }) => (
                    <ControlledPropertyInputs.String
                        readOnly={true}
                        nullable={false}
                        value={original.name}
                        style={{ paddingLeft: 0 }}
                        hideLabel={true}
                        propertyValidationInformation={{
                            resourceType: ResourceType.CONTENT,
                            propertyIdentifier: ContentPropertyValidation.NAME,
                            resourceId: undefined,
                        }}
                    />
                ),
            },
        ];
    }, []);

    return (
        <QueryBaseComponent name="Contents" query={contentsQuery}>
            {(data) => (
                <div
                    style={{
                        height: '100%',
                        minHeight: 485,
                        position: 'relative',
                        overflowY: 'auto',
                    }}
                >
                    <MantineTable
                        columns={columns}
                        data={data}
                        label={undefined}
                        enableActions={true}
                        initialPageSize={10}
                        fillParent={false}
                        rowActionsWidth={60}
                        rowActionsHeader={undefined}
                        tableOptions={{
                            enableSorting: true,
                            enableBottomToolbar: false,
                            enablePagination: false,
                            enableStickyHeader: true,
                            enableTopToolbar: true,
                            autoResetPageIndex: false,
                            renderRowActions: ({ row: { original, index } }) => (
                                <EditDeleteButtons
                                    key={index}
                                    handleEdit={() => openDrawerAtElement(original.modelElement)}
                                    handleDelete={() => {
                                        deleteResourceDialogFunction.deleteResource(
                                            original.id,
                                            original.name,
                                            ResourceType.CONTENT,
                                            deleteContent
                                        );
                                    }}
                                />
                            ),
                        }}
                    />
                </div>
            )}
        </QueryBaseComponent>
    );
};
